import {
	CyberSourceAccountCreatePaymentSubscriptionRequest,
	CyberSourceCreatePaymentSubscriptionRequest,
	CyberSourceCreatePaymentSubscriptionResponse,
	CyberSourceDeleteCardRequest,
	CyberSourceDeleteCardResponse,
	CyberSourceEnrollmentCheckRequest,
	CyberSourceEnrollmentCheckResponse,
	CyberSourceErrorResponse,
	CyberSourceFraudCheckRequest,
	CyberSourceFraudCheckResponse,
	CyberSourceGenerateFlexPublicKeyRequest,
	CyberSourceGenerateFlexPublicKeyResponse,
	CyberSourcePersistSavedCardDetailsRequest,
	CyberSourcePersistSavedCardDetailsResponse,
	CyberSourceRetrieveSavedCardsDetailsResponse,
} from './data-contracts';
import { ContentType, HttpClient, RequestParams, TraceDataType } from './http-client';

import { loggerCan } from '@/data/utils/loggerUtil';
import { logger } from '@/logging/logger';

export class CyberSource<SecurityDataType = unknown> {
	http: HttpClient<SecurityDataType>;

	constructor(http: HttpClient<SecurityDataType>) {
		this.http = http;
	}

	/**
	 * No description
	 *
	 * @tags CyberSource
	 * @name RetrieveSavedCardDetails
	 * @request GET:/store/{storeId}/hdm/cybersource/retrieveSavedCardDetails
	 * @response `200` `CyberSourceRetrieveSavedCardsDetailsResponse` Saved card details retrieved successfully
	 */
	retrieveSavedCardDetails = (storeId: string, params: RequestParams = {}) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['retrieveSavedCardDetails'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/hdm/cybersource/retrieveSavedCardDetails`,
				params: _params,
				methodName: 'retrieveSavedCardDetails',
			};
			logger.trace(traceData);
		}
		return this.http.request<CyberSourceRetrieveSavedCardsDetailsResponse, any>({
			path: `/store/${storeId}/hdm/cybersource/retrieveSavedCardDetails`,
			method: 'GET',
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * No description
	 *
	 * @tags CyberSource
	 * @name GenerateFlexPublicKey
	 * @request POST:/store/{storeId}/hdm/cybersource/generateFlexPublicKey
	 * @response `200` `CyberSourceGenerateFlexPublicKeyResponse` Flex capture context generated successfully
	 */
	generateFlexPublicKey = (
		storeId: string,
		data: CyberSourceGenerateFlexPublicKeyRequest,
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['generateFlexPublicKey'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/hdm/cybersource/generateFlexPublicKey`,
				params: _params,
				body: data,
				methodName: 'generateFlexPublicKey',
			};
			logger.trace(traceData);
		}
		return this.http.request<CyberSourceGenerateFlexPublicKeyResponse, any>({
			path: `/store/${storeId}/hdm/cybersource/generateFlexPublicKey`,
			method: 'POST',
			body: data,
			type: params.type ?? ContentType.Json,
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * No description
	 *
	 * @tags CyberSource
	 * @name CreatePaymentSubscription
	 * @request POST:/store/{storeId}/hdm/cybersource/createPaymentSubscription
	 * @response `200` `CyberSourceCreatePaymentSubscriptionResponse` Payment subscription created successfully
	 */
	createPaymentSubscription = (
		storeId: string,
		data: CyberSourceCreatePaymentSubscriptionRequest,
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['createPaymentSubscription'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/hdm/cybersource/createPaymentSubscription`,
				params: _params,
				body: data,
				methodName: 'createPaymentSubscription',
			};
			logger.trace(traceData);
		}
		return this.http.request<CyberSourceCreatePaymentSubscriptionResponse, any>({
			path: `/store/${storeId}/hdm/cybersource/createPaymentSubscription`,
			method: 'POST',
			body: data,
			type: params.type ?? ContentType.Json,
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * No description
	 *
	 * @tags CyberSource
	 * @name AccountCreatePaymentSubscription
	 * @request POST:/store/{storeId}/hdm/cybersource/accountCreatePaymentSubscription
	 * @response `200` `CyberSourceCreatePaymentSubscriptionResponse` Payment subscription created successfully from account
	 */
	accountCreatePaymentSubscription = (
		storeId: string,
		data: CyberSourceAccountCreatePaymentSubscriptionRequest,
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['accountCreatePaymentSubscription'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/hdm/cybersource/accountCreatePaymentSubscription`,
				params: _params,
				body: data,
				methodName: 'accountCreatePaymentSubscription',
			};
			logger.trace(traceData);
		}
		return this.http.request<CyberSourceCreatePaymentSubscriptionResponse, any>({
			path: `/store/${storeId}/hdm/cybersource/accountCreatePaymentSubscription`,
			method: 'POST',
			body: data,
			type: params.type ?? ContentType.Json,
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * No description
	 *
	 * @tags CyberSource
	 * @name DeleteCard
	 * @summary Delete Saved Card
	 * @request POST:/store/{storeId}/hdm/cybersource/deleteCard
	 * @response `200` `CyberSourceDeleteCardResponse` The requested completed successfully.
	 * @response `400` `CyberSourceErrorResponse` Bad request. The request could not be understood by the server due to malformed syntax.
	 * @response `401` `CyberSourceErrorResponse` Not authenticated. The user session is not valid.
	 * @response `403` `void` The user is not authorized to perform the specified request.
	 * @response `404` `void` The specified resource could not be found.
	 * @response `500` `CyberSourceErrorResponse` Internal server error. For details, see the server log files.
	 */
	deleteCard = (
		storeId: string,
		data: CyberSourceDeleteCardRequest,
		query?: {
			/**
			 * Language identifier. If not specified, the "locale" parameter is used. If "locale" is not specified, then the store default language is used.
			 * @example "-1, -2, -3, -4, -5"
			 */
			langId?: string;
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (loggerCan('trace') && (!this.http.traceDetails || this.http.traceDetails['deleteCard'])) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/hdm/cybersource/deleteCard`,
				params: _params,
				query: query,
				body: data,
				methodName: 'deleteCard',
			};
			logger.trace(traceData);
		}
		return this.http.request<CyberSourceDeleteCardResponse, CyberSourceErrorResponse | void>({
			path: `/store/${storeId}/hdm/cybersource/deleteCard`,
			method: 'POST',
			query: query,
			body: data,
			type: params.type ?? ContentType.Json,
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * No description
	 *
	 * @tags CyberSource
	 * @name PersistSavedCardDetails
	 * @request POST:/store/{storeId}/hdm/cybersource/persistSavedCardDetails
	 * @response `200` `CyberSourcePersistSavedCardDetailsResponse`
	 */
	persistSavedCardDetails = (
		storeId: string,
		data: CyberSourcePersistSavedCardDetailsRequest,
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['persistSavedCardDetails'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/hdm/cybersource/persistSavedCardDetails`,
				params: _params,
				body: data,
				methodName: 'persistSavedCardDetails',
			};
			logger.trace(traceData);
		}
		return this.http.request<CyberSourcePersistSavedCardDetailsResponse, any>({
			path: `/store/${storeId}/hdm/cybersource/persistSavedCardDetails`,
			method: 'POST',
			body: data,
			type: params.type ?? ContentType.Json,
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * No description
	 *
	 * @tags CyberSource
	 * @name FraudCheck
	 * @request POST:/store/{storeId}/hdm/cybersource/fraudCheck
	 * @response `200` `CyberSourceFraudCheckResponse`
	 */
	fraudCheck = (
		storeId: string,
		data: CyberSourceFraudCheckRequest,
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (loggerCan('trace') && (!this.http.traceDetails || this.http.traceDetails['fraudCheck'])) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/hdm/cybersource/fraudCheck`,
				params: _params,
				body: data,
				methodName: 'fraudCheck',
			};
			logger.trace(traceData);
		}
		return this.http.request<CyberSourceFraudCheckResponse, any>({
			path: `/store/${storeId}/hdm/cybersource/fraudCheck`,
			method: 'POST',
			body: data,
			type: params.type ?? ContentType.Json,
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * No description
	 *
	 * @tags CyberSource
	 * @name EnrollmentCheck
	 * @request POST:/store/{storeId}/hdm/cybersource/enrollmentCheck
	 * @response `200` `CyberSourceEnrollmentCheckResponse`
	 */
	enrollmentCheck = (
		storeId: string,
		data: CyberSourceEnrollmentCheckRequest,
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['enrollmentCheck'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/hdm/cybersource/enrollmentCheck`,
				params: _params,
				body: data,
				methodName: 'enrollmentCheck',
			};
			logger.trace(traceData);
		}
		return this.http.request<CyberSourceEnrollmentCheckResponse, any>({
			path: `/store/${storeId}/hdm/cybersource/enrollmentCheck`,
			method: 'POST',
			body: data,
			type: params.type ?? ContentType.Json,
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
}
